import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useToast } from '@shared/hooks/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PackagingApiAdapter } from '../adapters/PackagingApiAdapter';
import { CreatePackagingRequest } from '../models/Packaging';
import { PackagingService } from '../services/PackagingService';

interface UseUpdatePackagingOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UpdatePackagingVariables {
  id: number;
  data: CreatePackagingRequest;
}

export const useUpdatePackaging = (options: UseUpdatePackagingOptions = {}) => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new PackagingApiAdapter(httpClient);
  const packagingService = new PackagingService(apiAdapter);
  const { toast } = useToast();
  const { t } = useTranslation('materialAlmacenModule');

  const mutation = useMutation({
    mutationFn: ({ id, data }: UpdatePackagingVariables) => packagingService.updatePackaging(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materialAlmacen'] });

      toast({
        title: t('messages.update_success'),
        description: t('modal.edit.success_description'),
        variant: 'default',
      });

      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.log('🔍 useUpdatePackaging - Error:', error);

      toast({
        title: t('messages.update_error'),
        description: error.message || t('errors.unexpected'),
        variant: 'destructive',
      });

      options.onError?.(error);
    },
  });

  return {
    updatePackaging: (id: number, data: CreatePackagingRequest) => mutation.mutate({ id, data }),
    updatePackagingAsync: (id: number, data: CreatePackagingRequest) => mutation.mutateAsync({ id, data }),
    isUpdating: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};
